import { useT, useLocale, type Locale } from '@/i18n';
import { useSession } from '@/hooks/session';
import { api } from '@/api/client';
import { cx } from '@/components/ui/cx';

const OPTIONS: Array<{ code: Locale; label: string }> = [
  { code: 'en', label: 'EN' },
  { code: 'hi', label: 'हिं' },
];

/**
 * Segmented EN / हिं control. Persists to the account when signed in (fire-and-forget).
 * `compact` is the header size: smaller, still a 32px target (WCAG 2.2 asks for 24px).
 */
export const LanguageToggle = ({ className = '', compact = false }: { className?: string; compact?: boolean }) => {
  const t = useT();
  const { locale, setLocale } = useLocale();
  const { account } = useSession();

  const choose = (next: Locale) => {
    if (next === locale) return;
    setLocale(next);
    if (account) void api.me.update({ locale: next }).catch(() => {});
  };

  return (
    <div
      role="radiogroup"
      aria-label={t('nav.language')}
      className={cx('inline-flex rounded-md border border-sage-300 bg-white p-0.5', className)}
    >
      {OPTIONS.map((option) => {
        const checked = locale === option.code;
        return (
          <button
            key={option.code}
            type="button"
            role="radio"
            aria-checked={checked}
            onClick={() => choose(option.code)}
            className={cx(
              'rounded-sm transition-colors',
              compact ? 'min-h-8 min-w-8 px-2 text-caption font-bold' : 'min-h-tap min-w-tap px-3 text-label',
              checked ? 'bg-teal-600 text-white' : 'text-ink-muted hover:text-teal-600',
            )}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
};
